<script lang="ts" setup>
import { ref, computed, Ref, nextTick, inject, onBeforeMount, onBeforeUnmount, getCurrentInstance } from 'vue';
import { useEvents } from '@/plugins/events';
import { useRoute } from 'vue-router';
import { TableEntry, TableType, instaceOfTableEntry } from '.';
import { KeyValuePair, Resource } from '@/helpers/Interfaces';
import { v4 as uuidv4 } from 'uuid';
import { RootEntry } from '@/components/builder/form/entries/RootEntry';
import { FormBuilderContract } from '@/components/builder/form';
import { AggregateBlueprint } from '@/components/builder/base/blueprints/AggregateBlueprint';
import { AlwaysChoice } from '@/components/builder/form/enums/AlwaysChoice';
import { NeverChoice } from '@/components/builder/form/enums/NeverChoice';
import { WhenChoice } from '@/components/builder/form/enums/WhenChoice';
import { EntryFactory, instanceOfEntryFactory } from '@/components/builder/form/traits/EntryFactory';
import StorageService, { Resource as FileResource } from "@/modules/core/files/services/StorageService";
import BpmnService from '@/modules/low-code/process/services/BpmnService';
import TableTemplatesService from '@/modules/low-code/table-templates/services/TableTemplatesService';
import TableView from './TableView.vue';
import ImportTableModal from './modals/ImportTableModal.vue';
import TemplatesModal from './modals/TemplatesModal.vue';
import FieldCanAddRow from './properties/FieldCanAddRow.vue';
import FieldCanDeleteRow from './properties/FieldCanDeleteRow.vue';
import properties from '@/components/forms/properties';
import { FieldScript } from '@/components/forms/properties';
import { DocumentType } from '../logito-document';
import { ScriptTypeEnum } from '@/modules/low-code/services/ScriptsService';
import { cloneDeep } from 'lodash';
import { useControlReload } from '@/components/forms/helpers/RelatedControlsHelper';
import { useDynamicTrigger } from '@/components/forms/helpers/DynamicTriggerHelper';
import { TriggerMoments, TriggerModes } from '@/components/builder/form/traits/HasTriggers';
import { FieldTrigger } from '@/components/forms/properties';
import { registerComponent, unRegisterComponent } from '@/store/triggers';
import { createTriggerComponentCtx } from '@/components/builder/form/traits/TriggerContext';

defineOptions({
    name: 'table-blueprint',
    components: {
        FieldScript,
        ...properties,
    },
});

const props = defineProps({
  "blueprint": null,
  "entry": null,
  "form": null,
  "parent": null
});

const { $events } = useEvents();
const level = inject<number>('level', 0);

const reloadKey = ref(0);
const requestedActiveRowIndex = ref<number | null>(null);
const requestedActiveRowVersion = ref(0);
const entryData = ref(new TableEntry()) as Ref<TableEntry>;
const idsRows = ref<string[]>([]);
const templates = ref<Resource<KeyValuePair>[]>([]);
const templateFile = ref<FileResource>();
const route = useRoute();

const importTableModalRef = ref<InstanceType<typeof ImportTableModal | null>>(null);
const templatesModalRef = ref<InstanceType<typeof TemplatesModal | null>>(null);
const isExternalForm = computed(() => route.meta.externalAccess === true);

const triggerComponentCtx = createTriggerComponentCtx(
    {

    }
);

const value = computed({
    get()
    {
        const { data: value } = entryData.value;

        return value;
    },
    set(value: Record<string, any>[])
    {
        entryData.value.data = value;
    },
});

const blueprint = computed(() => props.blueprint);
const design = computed(() => props.form.designMode());
const required = computed(() => props.form.expressions.required(props.blueprint));
const isTemplateMode = computed(() => !!props.entry.fieldIndex);
const isImport = computed(() => blueprint.value.allowImport && !isTemplateMode.value);
const isTemplates = computed(() => templates.value.length > 0 && !isTemplateMode.value);
const scriptType = computed(() => ScriptTypeEnum.DynamicTableDefaultRowValue);
const documentComponents = computed(() =>
    props.form.schema
        .components(["document", "logito-attachments"])
        .map((p) => ({
            value: p.name,
            text: props.form.localization.translate((p as DocumentType).label),
        }))
);

const canAddRow = computed(() =>
{
    if (design.value) return blueprint.value.canAddRow !== NeverChoice.Never;

    switch (blueprint.value.canAddRow)
    {
        case AlwaysChoice.Always:
            return true;
        case NeverChoice.Never:
            return false;
        case WhenChoice.When:
            return props.form.expressions.executeExpression(blueprint.value.canAddRowWhen) ?? true;
        default:
            return true;
    }
});

const canDeleteRow = (index: number) =>
{
    if (design.value) return blueprint.value.canDeleteRow !== NeverChoice.Never;

    switch (blueprint.value.canDeleteRow)
    {
        case AlwaysChoice.Always:
            return true;
        case NeverChoice.Never:
            return false;
        case WhenChoice.When:
            return props.form.expressions.executeExpression(blueprint.value.canDeleteRowWhen, index) ?? true;
        default:
            return true;
    }
};

const moduleId = computed(() =>
{
    return route.params.moduleId.toString();
});

onBeforeMount(() =>
{
    if (!props.form.designMode())
    {
        useControlReload($events, props.blueprint.name, level).eventOn(setControlData);
        registerComponent(props.form.blueprint.id, props.blueprint.name, (getCurrentInstance()?.props), triggerComponentCtx);
    }
});

onBeforeUnmount(() =>
{
    if (!props.form.designMode())
    {
        useControlReload($events, props.blueprint.name, level).eventOff();
        unRegisterComponent(props.form.blueprint.id, props.blueprint.name);
    }
});

const loadControlData = () =>
{
    return props.form.document.initEntry(props.blueprint, entryData.value, instaceOfTableEntry);
};

const setControlData = async (tableRowIndex: number | null = null) =>
{
    entryData.value = loadControlData();

    if (value.value.length === 0) return;

    initRowsEntries();

    idsRows.value = Array(value.value.length ?? blueprint.value.numberOfRows);

    for (let i = 0; i < idsRows.value.length; i++)
    {
        idsRows.value[i] = uuidv4();
    }

    const firstRow = value.value[0];
    const columns = Object.keys(firstRow);

    if (tableRowIndex != null)
        columns.forEach(column =>
        {
            const i = tableRowIndex;

            useControlReload($events, column, level, props.blueprint.name, i).emitReload();

            if (value.value[i][column].type === "related-document")
                $events.$emit(`reload-related-documents-${props.blueprint.name}-${i}-${column}`);
        });
};

const openTemplatesModal = () =>
{
    templatesModalRef.value?.showModal(templates.value);
};

const openImportModal = () =>
{
    importTableModalRef.value?.showModal();
};

const onAllowImportChange = (value: boolean) =>
{
    if (!value) blueprint.value.importColumns = {};
};

const getDefaultRows = async (indices: number[]) =>
{
    return BpmnService.getDefaultRowsValue(
        props.entry.module,
        props.entry.formId,
        props.blueprint.name,
        indices,
        entryData.value
    );
};

const setDefaultRowValues = async (indices: number[]): Promise<boolean> =>
{
    if (!blueprint.value.defaultRowValueScript?.key || design.value || isTemplateMode.value) return false;

    const defaultRows = await getDefaultRows(indices);

    Object.entries(defaultRows).forEach(([rowIndex, rowValue]) =>
    {
        if (!rowValue) return false;

        const row = entryData.value.data[Number.parseInt(rowIndex)];

        Object.entries(rowValue).forEach(([key, value]) =>
        {
            if (!value) return false;

            const elementBlueprint = props.form.schema.find(key) as EntryFactory<any>;

            if (!elementBlueprint) return false;

            row[key] = elementBlueprint.createEntry(value);
        });
    });

    props.form.update();

    return true;
};

const userFeatureKeyMap: Record<string, number> = {
    Director: 1,
    Manager: 2,
    Superior: 3,
    Subordinate: 4,
    DirectSubordinate: 5,
    NoLogon: 6,
};

const normalizeUserEntryMeta = (component: any, entry: any): void =>
{
    if (component?.type !== 'user' || !entry) return;

    if (entry.valuesType != null) return;

    const firstItem = Array.isArray(entry.data) ? entry.data[0] : null;
    const rawType = firstItem?.type;

    if (typeof rawType === 'number')
    {
        entry.valuesType = rawType;

        return;
    }

    if (typeof rawType === 'string' && rawType in userFeatureKeyMap)
    {
        entry.valuesType = userFeatureKeyMap[rawType];

        return;
    }

    const selectedTypeKey = Object.keys(component?.userTypes ?? {}).find((key) => component.userTypes?.[key] === true);

    if (selectedTypeKey && selectedTypeKey in userFeatureKeyMap)
    {
        entry.valuesType = userFeatureKeyMap[selectedTypeKey];
    }
};

const normalizeUserGroupEntryMeta = (component: any, entry: any): void =>
{
    if (component?.type !== 'user-group' || !entry) return;

    if (entry.settings?.key != null) return;

    const firstItem = Array.isArray(entry.data) ? entry.data[0] : null;
    const rawType = firstItem?.type;

    if (typeof rawType === 'number')
    {
        entry.settings = { key: rawType, value: entry.settings?.value ?? '' };
    }
};

const initRowsEntries = (): void =>
{
    const rows = value.value;

    for (let rowIndex = 0; rowIndex < rows.length; rowIndex++)
    {
        rows[rowIndex] ??= {};

        for (const component of blueprint.value.components)
        {
            if (!instanceOfEntryFactory(component)) continue;

            const original = rows[rowIndex][component.name];

            if (original && typeof original.validate === 'function') continue;

            const entry = component.createEntry(original?.data ?? null);

            const normalizedEntry = original && typeof original === 'object'
                ? Object.assign(entry, original)
                : entry;

            normalizeUserEntryMeta(component, normalizedEntry);
            normalizeUserGroupEntryMeta(component, normalizedEntry);

            normalizedEntry.index = rowIndex;

            rows[rowIndex][component.name] = normalizedEntry;
        }
    }
};

const requestActivateRow = (index: number): void =>
{
    requestedActiveRowIndex.value = index;
    requestedActiveRowVersion.value += 1;
};

const setDefaultNumberOfRows = (_value: number) =>
{
    value.value.length = _value;
    idsRows.value.length = _value;
    blueprint.value.numberOfRows = _value;

    for (let i = 0; i < value.value.length; i++)
    {
        if (value.value[i] == null)
        {
            value.value[i] = {};
            idsRows.value[i] = uuidv4();
        }
    }

    initRowsEntries();
};

const addRow = async () =>
{
    value.value.push({});
    idsRows.value.push(uuidv4());

    initRowsEntries();

    if (await setDefaultRowValues([value.value.length - 1]))
        reloadKey.value++;

    requestActivateRow(idsRows.value.length - 1);

    useDynamicTrigger($events, props.form.blueprint.id).emit(TriggerMoments.onChange, props.blueprint, props.parent?.type === 'table' ? (idsRows.value.length - 1) : null);
};

const copyColumnData = (sourceRow: Record<string, any>, targetRow: Record<string, any>, columnKey: string): void =>
{
    if (!sourceRow[columnKey]?.data || !targetRow[columnKey]) return;

    targetRow[columnKey].data = sourceRow[columnKey].data;
};

const copyRow = async (index: number) =>
{
    value.value.splice(index + 1, 0, cloneDeep(value.value[index]));
    idsRows.value.splice(index + 1, 0, uuidv4());

    for (const key in value.value[index + 1])
    {
        if (value.value[index + 1][key] && typeof value.value[index + 1][key] === 'object' && 'index' in value.value[index + 1][key])
            value.value[index + 1][key].index = index + 1;
    }

    initRowsEntries();
    requestActivateRow(index + 1);

    useDynamicTrigger($events, props.form.blueprint.id).emit(TriggerMoments.onChange, props.blueprint, props.parent?.type === 'table' ? index : null);
};

const removeRow = async (index: number) =>
{
    useDynamicTrigger($events, props.form.blueprint.id).emit(TriggerMoments.onChange, props.blueprint, props.parent?.type === 'table' ? index : null);
};

const copyBelow = async (index: number, colIndex: number): Promise<void> =>
{
    copyCol(index, value.value.length - 1, 1, colIndex);
};

const copyBelowMulti = async (startRowIndex: number, endRowIndex: number, colIndex: number): Promise<void> =>
{
    if (startRowIndex === endRowIndex) return;

    const direction = startRowIndex < endRowIndex ? 1 : -1;

    copyCol(startRowIndex, endRowIndex, direction, colIndex);
};

const copyCol = async (startRowIndex: number, endRowIndex: number, direction: number, colIndex: number): Promise<void> =>
{
    if (startRowIndex === endRowIndex) return;

    const sourceRow = value.value[startRowIndex];
    const columnKey = Object.keys(sourceRow)[colIndex];

    if (!columnKey) return;

    const targetIndex = endRowIndex;

    for (let i = startRowIndex + direction; i !== targetIndex + direction; i += direction)
    {
        if (value.value.length <= i) break;

        copyColumnData(sourceRow, value.value[i], columnKey);
        useControlReload($events, columnKey, level, props.blueprint.name, i).emitReload();

        if (value.value[i][columnKey].type === "related-document")
            $events.$emit(`reload-related-documents-${props.blueprint.name}-${i}-${columnKey}`);
    }
};

const fetchTemplates = async () =>
{
    if (!props.entry.module || !props.entry.formId || !props.blueprint.name || isExternalForm.value) return;

    try
    {
        const response = await TableTemplatesService.getTemplates(
            props.entry.module,
            props.entry.formId,
            props.blueprint.name
        );

        templates.value = response.items;
    }
    catch (ex)
    {
        templates.value = [];
    }
};

const uploadTemplate = async () =>
{
    if (templateFile.value && templateFile.value.upload)
    {
        try
        {
            const response = await StorageService.upload(templateFile.value.upload, 'users', true, false, false, 'public');

            blueprint.value.templateFileId = response.publicId;
        }
        catch (ex)
        {
            blueprint.value.templateFileId = null;
        }
    }
    else
    {
        blueprint.value.templateFileId = null;
    }
};

const initTemplateFile = async () =>
{
    if (blueprint.value.templateFileId)
    {
        const response = await StorageService.fetchResourceSimple(blueprint.value.templateFileId);

        templateFile.value = response;
    }
};

const init = async () =>
{
    entryData.value = props.form.document.initEntry(props.blueprint, entryData.value, instaceOfTableEntry);

    // TODO: Do not mutate props
    props.blueprint.importColumns ??= {}; // eslint-disable-line

    if (value.value.length === 0)
    {
        const numberOfRows = blueprint.value.numberOfRows;

        await nextTick();

        for (let i = 0; i < numberOfRows; i++)
        {
            value.value.push({});
        }

        await setDefaultRowValues(Array.from(Array(numberOfRows).keys()));
    }

    idsRows.value = Array(value.value.length ?? blueprint.value.numberOfRows);

    for (let i = 0; i < idsRows.value.length; i++)
    {
        idsRows.value[i] = uuidv4();
    }

    initRowsEntries();

    fetchTemplates();
    await initTemplateFile();
};

const afterImportCompleted = async (tableEntry: TableEntry, file: FileResource) =>
{
    value.value = tableEntry.data;
    initRowsEntries();
    idsRows.value = Array(value.value.length ?? blueprint.value.numberOfRows);

    for (let i = 0; i < idsRows.value.length; i++)
    {
        idsRows.value[i] = uuidv4();
    }

    props.form.update();

    await setDefaultRowValues(Array.from(Array(entryData.value.data.length).keys()));
    reloadKey.value = reloadKey.value + 1;

    if (blueprint.value.saveImportFileToField && file && props.entry.hasOwnProperty(blueprint.value.saveImportFileToField))
    {
        let data: any[] | any = [file];
        const entryData = props.entry[blueprint.value.saveImportFileToField];

        if (entryData.type == 'document' && file.upload)
        {
            const res = await StorageService.upload(file.upload, 'documents');

            data = {
                name: res.name,
                publicId: res.publicId,
                thumb: null,
                url: res.url,
            };
        }

        entryData.data = data;
    }

    useDynamicTrigger($events, props.form.blueprint.id).emit(TriggerMoments.onChange, props.blueprint, null);
};

init();
</script>

<template>
    <form-component-wrapper
        class="table-component"
        :form="form"
        :parent="parent"
        :blueprint="blueprint"
        :disabled="false"
    >
        <template #default>
            <div class="form-group">
                <form-label :form="form" :blueprint="blueprint" :required="required" :entry="entryData" />
                <table-view
                    v-model:value="value"
                    v-model:ids-rows="idsRows"
                    :form="form"
                    :blueprint="blueprint"
                    :entry="entry"
                    :design="design"
                    :can-add-row="canAddRow"
                    :can-delete-row="canDeleteRow"
                    :optimize-row-rendering="blueprint.optimizeRowRendering"
                    :requested-active-row-index="requestedActiveRowIndex"
                    :requested-active-row-version="requestedActiveRowVersion"
                    :reload-key="reloadKey"
                    @copyRow="copyRow"
                    @copyBelow="copyBelow"
                    @copyBelowMulti="copyBelowMulti"
                    @removeRow="removeRow"
                />
                <form-help :form="form" :blueprint="blueprint" />
                <form-error-message :entry="entryData" name="values" />
                <form-error-message :entry="entryData" name="custom" />
                <div class="d-flex flex-wrap gap-2 my-2">
                    <action-button
                        v-if="canAddRow"
                        variant="success"
                        :disabled="design"
                        class="flex-fill me-0"
                        icon="fas fa-plus"
                        :text="$t('[[[Dodaj wiersz]]]')"
                        @click="addRow"
                    />
                    <action-button
                        v-if="isImport"
                        class="flex-fill me-0"
                        variant="light"
                        :disabled="design"
                        icon="fas fa-file-import"
                        :text="$t('[[[Importuj]]]')"
                        @click="openImportModal"
                    ></action-button>
                    <ideo-button
                        v-if="isTemplates"
                        variant="secondary"
                        :title="$t('[[[Szablony]]]')"
                        icon="fas fa-bars"
                        @click="openTemplatesModal"
                    />
                </div>
            </div>
            <import-table-modal
                ref="importTableModalRef"
                :blueprint-name="blueprint.name"
                :form-schema="form"
                :entry="entry"
                @import-completed="afterImportCompleted"
            />
            <templates-modal ref="templatesModalRef" :blueprint-name="blueprint.name" :entry="entry" :form="form" />
        </template>
        <template #properties>
            <field-name v-model="blueprint.name" :form="form" :blueprint="blueprint" />
            <IdeoTabs pills nav-class="nav-justified" content-class="mt-3 docs-container">
                <IdeoTab :title="$t('[[[Konfiguracja]]]')" active>
                    <ideo-form-localize v-slot="{ locale }">
                        <field-text v-model="blueprint.label[locale]" :label="$t('[[[Etykieta]]]')" />
                    </ideo-form-localize>
                    <field-checkbox v-model="blueprint.showLabel" :label="$t('[[[Wyświetlaj etykietę]]]')" />
                    <field-columns
                        v-model="blueprint.components"
                        v-model:columns="blueprint.columns"
                        :form="form"
                        :blueprint="blueprint"
                        :label="$t('[[[Kolumny]]]')"
                        :schema="form.schema"
                    />
                    <field-numeric
                        :model-value="blueprint.numberOfRows"
                        :label="$t('[[[Początkowa ilość wierszy]]]')"
                        :naturals="true"
                        :min="0"
                        :max="20"
                        @update:model-value="setDefaultNumberOfRows"
                    />
                    <field-checkbox
                        v-model="blueprint.allowImport"
                        :label="$t('[[[Pozwól na uzupełnianie danych z pliku excel]]]')"
                        @update:model-value="onAllowImportChange"
                    />
                    <field-checkbox
                        v-if="blueprint.allowImport"
                        v-model="blueprint.hasColumnHeaders"
                        :label="$t('[[[Pomiń pierwszy wiersz excela]]]')"
                    />
                    <field-checkbox
                        v-model="blueprint.showColumnOrdinalNumber"
                        :label="$t('[[[Wyświetlaj kolumnę z liczbą porządkową]]]')"
                    />
                    <div class="d-flex align-items-center gap-2">
                        <field-checkbox
                            v-model="blueprint.optimizeRowRendering"
                            :label="$t('[[[Włącz zoptymalizowane renderowanie wierszy]]]')"
                            class="flex-grow-1"
                        />
                        <i
                            class="fas fa-circle-info text-muted"
                            v-tooltip="$t('[[[Wybranie tej opcji spowoduje, że wszystkie wiersze tabeli poza aktywnie edytowanym wyświetlane będą jako sam tekst zamiast pełnej kontrolki]]]')"
                        />
                    </div>
                    <field-select
                        v-if="blueprint.allowImport"
                        :options="documentComponents"
                        v-model="blueprint.saveImportFileToField"
                        :label="$t('[[[Zapisz importowany plik do pola]]]')"
                    />
                    <field-map-select-input
                        v-if="blueprint.allowImport"
                        v-model="blueprint.importColumns"
                        :label="$t('[[[Pola mapowane (excel)]]]')"
                        :form="form"
                        :blueprint="blueprint"
                    />
                    <ideo-form-group :label="'Wgraj indywidualny szablon do pobrania'">
                        <fileupload :max-files="1" v-model="templateFile" @update:modelValue="uploadTemplate" />
                    </ideo-form-group>
                    <field-computed
                        v-model="blueprint.computedFields"
                        :blueprint="blueprint"
                        :form="form"
                        :label="$t('[[[Pola wyliczane]]]')"
                    />
                    <FieldScript
                        v-model="blueprint.defaultRowValueScript"
                        :show-label="false"
                        :label="$t('[[[Skrypt dla wartości domyślnych]]]')"
                        :endpoint="`admin/forms/scripts`"
                        :script-type="scriptType"
                        :module-id="moduleId"
                    />
                    <ideo-form-localize v-slot="{ locale }">
                        <field-textarea v-model="blueprint.help[locale]" :label="$t('[[[Pomoc]]]')" />
                    </ideo-form-localize>
                    <field-can-add-row :form="form" :blueprint="blueprint" :label="$t('[[[Pozwól na dodawanie wierszy]]]')" />
                    <field-can-delete-row :form="form" :blueprint="blueprint" :label="$t('[[[Pozwól na usuwanie wierszy]]]')" />
                    <field-visible :form="form" :blueprint="blueprint" :label="$t('[[[Widoczność]]]')" />
                    <field-required :form="form" :blueprint="blueprint" :label="$t('[[[Pole wymagane]]]')" />
                    <field-error :form="form" :blueprint="blueprint" />
                </IdeoTab>
                <IdeoTab :title="$t('[[[Wyzwalacze]]]')">
                    <FieldTrigger v-model="blueprint.serverSideTriggers" :mode="TriggerModes.ServerSide" :blueprint="blueprint" />
                    <FieldTrigger v-model="blueprint.clientSideTriggers" :mode="TriggerModes.ClientSide" :blueprint="blueprint" />
                </IdeoTab>
            </IdeoTabs>
        </template>
    </form-component-wrapper>
</template>
